import React, { useState, useEffect } from 'react';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import RemoveIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import Card from '@material-ui/core/Card';
import TextField from '@material-ui/core/TextField';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import { isEqual } from 'lodash';
import { updateFamily } from '../../api/familiesAPI';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
  longTextField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '60%',
    minWidth: 200,
  },
  mediumTextField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '30%',
    minWidth: 200,
  },
  button: {
    margin: 5
  }
}));

export default function EditDetailsCard({ ...props }) {
  const classes = useStyles();
  const { 
    handleCancel,
    setIsSnackbarOpen,
    setSnackbarVariant,
    setSnackbarMessage,
    handleRecordUpdate,
    family } = props;
  const [lastName, setLastName] = useState('');
  const [adults, setAdults] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [emails, setEmails] = useState([]);
  const [phones, setPhones] = useState([]);
  const [address, setAddress] = useState([]);
  const [province, setProvince] = useState([]);
  const [postal, setPostal] = useState([]);
  const [city, setCity] = useState([]);
  
  useEffect(() => {
    setLastName(family.lastName);
    setAdults([...family.adults]);
    setDependents([...family.dependents]);
    setEmails([...family.emails]);
    setPhones([...family.phones]);
    setAddress(family.address);
    setProvince(family.province);
    setPostal(family.postal);
    setCity(family.city);
  }, [family]);

  const handleSave = () => {
    const newFamily = {
      id: family.id,
      lastName,
      adults,
      dependents,
      emails,
      phones,
      address,
      province,
      postal,
      city
    };
    if (isEqual(newFamily, family)) {
      setSnackbarVariant('warning');
      setSnackbarMessage('No changes detected. Maybe you meant to cancel?');
      setIsSnackbarOpen(true);
    } else {
      try {
        updateFamily(newFamily);
        handleRecordUpdate(newFamily);
        setSnackbarVariant('success');
        setSnackbarMessage('Contact information saved.');
        setIsSnackbarOpen(true);
      } catch (e) {
        setSnackbarVariant('error');
        setSnackbarMessage('Unable to save. Please try again later.');
        setIsSnackbarOpen(true);
      }
    }
  };
  const handleNewRecordCancel = () => {
    setLastName(family.lastName);
    setAdults(family.adults);
    setDependents(family.dependents);
    setEmails(family.emails);
    setPhones(family.phones);
    setAddress(family.address);
    setProvince(family.province);
    setPostal(family.postal);
    setCity(family.city);
    handleCancel();
  }
  const handleAdultAdd = () => {
    adults.push('');
    emails.push('');
    phones.push('');
    setAdults([...adults]);
    setEmails([...emails]);
    setPhones([...phones]);
  }
  const handleDependentAdd = () => {
    dependents.push('');
    setDependents([...dependents]);
  }
  const handleAdultDelete = (index) => {
    adults.splice(index, 1);
    emails.splice(index, 1);
    phones.splice(index, 1);
    setAdults([...adults]);
    setEmails([...emails]);
    setPhones([...phones]);
  }
  const handleDependentDelete = (index) => {
    dependents.splice(index, 1);
    setDependents([...dependents]);
  }
  const handleEmailDelete = (index) => {
    emails.splice(index, 1);
    setEmails([...emails]);
  }
  const handlePhoneDelete = (index) => {
    phones.splice(index, 1);
    setPhones([...phones]);
  }
  const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
  }
  const handleChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case "Last Name":
        setLastName(capitalize(value));
        break;
      case "Address":
        setAddress(value);
        break;
      case "City":
        setCity(capitalize(value));
        break;
      case "Province/State":
        setProvince(value.toUpperCase().substring(0, 2));
        break;
      case "Postal/ZIP Code":
        setPostal(value.replace(/\s/g, ''));
        break;
      default:
        console.debug("unknown name", name, value);
    }
  }
  const handleIndexedChange = (entity, value, index) => {
    switch (entity) {
      case "Adult":
        adults[index] = capitalize(value);
        setAdults([...adults]);
        break;
      case "Dependent":
        dependents[index] = capitalize(value);
        setDependents([...dependents]);
        break;
      case "Email":
        emails[index] = value.replace(/\s/g, '');
        setEmails([...emails]);
        break;
      case "Phone":
        phones[index] = value.replace(/\D/g, '');
        setPhones([...phones]);
        break;
      default: 
        console.debug("unknown entity:", value, index);
    }
  }
  const nameErrorMessage = (name) => {
    if (!name) {
      return "Enter a name."
    } else if (!/^[a-zA-Z]+$/.test(name)) {
      return "Only alphabetical characters allowed.";
    } else {
      return '';
    }
  }
  return (
    <React.Fragment>
      <Grid item sm={12}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
          <Grid item>
            <TextField
              required
              error={!!nameErrorMessage(lastName)}
              helperText={nameErrorMessage(lastName)}
              name="Last Name"
              id="lastName"
              label="Last Name"
              value={lastName}
              onChange={handleChange}
              className={classes.longTextField}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            {!adults.length && 
              <Button 
                className={classes.button}
                variant="outlined" 
                color="primary"
                onClick={handleAdultAdd}
              >
                Add Adult
              </Button>
            }
            {adults.map((adult, index) => 
              <TextField
                key={index}
                required
                error={!!nameErrorMessage(adult)}
                helperText={nameErrorMessage(adult)}
                id="adults"
                label="First Name"
                value={adult}
                onChange={(event) => handleIndexedChange("Adult", event.target.value, index)}
                className={classes.textField}
                margin="normal"
                variant="outlined"
                InputProps={{
                  startAdornment: 
                  adults.length === 2 ? (
                    <InputAdornment position="start">
                      <IconButton 
                        size="small"
                        color="primary"
                        aria-label="search"
                        onClick={() => handleAdultDelete(index)}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : '',
                  endAdornment: 
                  adults.length === index + 1 && adults.length === 1 ? (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small"
                        color="primary"
                        aria-label="search"
                        onClick={handleAdultAdd}
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : '',
                }}
              />
            )}
          </Grid>
          <Grid item>
            {!dependents.length && 
              <Button 
                className={classes.button}
                variant="outlined" 
                color="primary"
                onClick={handleDependentAdd}
              >
                Add Dependent
              </Button>
            }
            {dependents.map((dependent, index) => 
              <TextField
                required
                key={index}
                error={!!nameErrorMessage(dependent)}
                helperText={nameErrorMessage(dependent)}
                id="dependents"
                label="Dependent First Name"
                value={dependent}
                onChange={(event) => handleIndexedChange("Dependent", event.target.value, index)}
                className={classes.textField}
                margin="normal"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton 
                        size="small"
                        color="primary"
                        aria-label="search"
                        onClick={() => handleDependentDelete(index)}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small"
                        color="primary"
                        aria-label="search"
                        onClick={handleDependentAdd}
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
           </Grid>
           <Grid item>
            <TextField
              required
              label="Address"
              name="Address"
              value={address}
              onChange={handleChange}
              className={classes.longTextField}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <TextField
              required
              label="City"
              name="City"
              value={city}
              onChange={handleChange}
              className={classes.textField}
              margin="normal"
              variant="outlined"
            />
            <TextField
              required
              label="Province/State"
              name="Province/State"
              value={province}
              onChange={handleChange}
              className={classes.textField}
              margin="normal"
              variant="outlined"
            />
            <TextField
              required
              label="Postal/ZIP Code"
              name="Postal/ZIP Code"
              value={postal}
              onChange={handleChange}
              className={classes.textField}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            {emails.map((email, index) => 
              <TextField
                key={`email-${index}`}
                label={`${adults[index]}'s Email`}
                value={email}
                onChange={(event) => handleIndexedChange("Email", event.target.value, index)}
                className={classes.mediumTextField}
                margin="normal"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton 
                        size="small"
                        color="primary"
                        aria-label="search"
                        onClick={handleEmailDelete}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Grid>
          <Grid item>
            {phones.map((phone, index) =>
              <TextField
              key={`phone-${index}`}
                label={`${adults[index]}'s phone`}
                value={phone}
                onChange={(event) => handleIndexedChange("Phone", event.target.value, index)}
                className={classes.mediumTextField}
                margin="normal"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton 
                        size="small"
                        color="primary"
                        aria-label="search"
                        onClick={handlePhoneDelete}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Grid>
          </CardContent>
          <CardActions>
            <IconButton
              color='primary'
              aria-label="save"
              onClick={handleSave}
            >
              <SaveIcon />
            </IconButton>
            <IconButton
              color='primary'
              aria-label="cancel"
              onClick={handleNewRecordCancel}
            >
              <CancelIcon />
            </IconButton>
          </CardActions>
        </Card>
      </Grid>
    </React.Fragment>
  )
}